import {
  Button as ReactAriaButton,
  Text as ReactAriaText,
  UNSTABLE_Toast as ReactAriaToast,
  UNSTABLE_ToastContent as ReactAriaToastContent,
  UNSTABLE_ToastRegion as ReactAriaToastRegion,
  UNSTABLE_ToastQueue as ToastQueue,
} from "react-aria-components";
import { flushSync } from "react-dom";

import { useI18n } from "../use-i18n";
import { cn, cv } from "../utils";
import {
  NcIconCross,
  NcIconExclamationCircle,
  NcIconInformationCircle,
  NcIconTickCircle,
} from "./nc-icons";

export type NcToastVariant = "default" | "success" | "danger";

export interface NcToastContent {
  variant?: NcToastVariant;
  title: string;
  description?: string;
}

const SUCCESS_INFO_TIMEOUT = 5000;

const supportsViewTransitions =
  typeof document !== "undefined" && "startViewTransition" in document;

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

// A transition still in flight when another update arrives (e.g. two toasts timing
// out a few hundred ms apart). Starting a second document.startViewTransition() would
// preempt/abort the first per spec, snapping its in-progress animation instead of
// letting it finish - the "flicker" this was built to avoid. So while one is running,
// later updates apply immediately with no animation rather than fighting over one.
let activeTransition: ReturnType<typeof document.startViewTransition> | null = null;

/**
 * A single queue shared by the whole app: `useToast` enqueues onto it from anywhere
 * (including outside React, e.g. a mutation's onError), and `NcToastRegion` is the one
 * place that subscribes to it and renders. Mount `NcToastRegion` once near the app root.
 *
 * `wrapUpdate` animates add/remove/reorder with the View Transitions API rather than
 * CSS transitions, since RAC's alpha Toast doesn't yet support exit-animation forwarding
 * (unlike NcModal/NcDrawer, a closed toast unmounts immediately). `flushSync` forces the
 * resulting re-render to happen synchronously inside the transition callback, which
 * `document.startViewTransition` requires to capture the "after" snapshot.
 */
export const ncToastQueue = new ToastQueue<NcToastContent>({
  maxVisibleToasts: 5,
  wrapUpdate: fn => {
    if (!supportsViewTransitions || activeTransition || prefersReducedMotion()) {
      fn();
      return;
    }
    const transition = document.startViewTransition(() => flushSync(fn));
    activeTransition = transition;
    const clearActive = () => {
      if (activeTransition === transition) activeTransition = null;
    };
    // The browser rejects these if it aborts the transition (e.g. the document isn't
    // visible) - that's fine, fn() already ran above via flushSync.
    transition.ready.catch(() => undefined);
    transition.updateCallbackDone.catch(() => undefined);
    transition.finished.catch(() => undefined).then(clearActive);
  },
});

export const useToast = () => {
  const enqueue = (content: NcToastContent, options?: { timeout?: number }) =>
    ncToastQueue.add(content, options);

  return {
    enqueueSuccess: (title: string, description?: string) =>
      enqueue({ variant: "success", title, description }, { timeout: SUCCESS_INFO_TIMEOUT }),
    // Errors persist until dismissed - they don't get a timeout.
    enqueueError: (title: string, description?: string) =>
      enqueue({ variant: "danger", title, description }),
    enqueueInfo: (title: string, description?: string) =>
      enqueue({ variant: "default", title, description }, { timeout: SUCCESS_INFO_TIMEOUT }),
  };
};

const toastVariants = cv(
  [
    "relative grid grid-cols-[auto_1fr_auto] items-start gap-2 rounded-md border bg-light px-3 py-3 pl-5 pr-3 shadow-lg min-w-0",
    "before:content-[''] before:absolute before:top-0 before:bottom-0 before:left-[-1px] before:w-1.5 before:rounded-l-md",
  ],
  {
    variants: {
      variant: {
        default: "border-main/45 before:bg-main",
        success: "border-success/45 before:bg-success",
        danger: "border-danger/45 before:bg-danger",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const iconColourVariant: Record<NcToastVariant, string> = {
  default: "text-main",
  success: "text-success",
  danger: "text-danger",
};

const getToastIcon = (t: (value: string) => string, variant: NcToastVariant) => {
  switch (variant) {
    case "success":
      return <NcIconTickCircle alt="" />;
    case "danger":
      return <NcIconExclamationCircle alt={t("alert")} />;
    default:
      return <NcIconInformationCircle alt="" />;
  }
};

export const NcToastRegion = () => {
  const { t } = useI18n();

  return (
    <ReactAriaToastRegion
      queue={ncToastQueue}
      aria-label="Notifications"
      className="fixed inset-x-0 top-4 z-50 mx-auto flex w-full max-w-sm flex-col items-center gap-2 px-4"
    >
      {({ toast }) => {
        const variant = toast.content.variant ?? "default";
        return (
          <ReactAriaToast
            toast={toast}
            className={cn(toastVariants({ variant }), "w-full")}
            style={{
              viewTransitionName: `nc-toast-${toast.key}`,
              viewTransitionClass: "nc-toast",
            }}
          >
            <div className={cn("pt-1 leading-none", iconColourVariant[variant])}>
              {getToastIcon(t, variant)}
            </div>
            <ReactAriaToastContent className="flex flex-col gap-1">
              <ReactAriaText slot="title" className="text-lg font-bold">
                {toast.content.title}
              </ReactAriaText>
              {toast.content.description && (
                <ReactAriaText slot="description">{toast.content.description}</ReactAriaText>
              )}
            </ReactAriaToastContent>
            <ReactAriaButton
              slot="close"
              className={cn("cursor-pointer p-1", iconColourVariant[variant])}
            >
              <NcIconCross />
            </ReactAriaButton>
          </ReactAriaToast>
        );
      }}
    </ReactAriaToastRegion>
  );
};
